import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDateOnly } from "@alm/domain";
import { AlertTriangle } from "@/lib/icons";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { FillPill } from "@/components/app/patterns/fill-pill";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { useFacility } from "@/lib/facility/facility";
import { orpc, type orpcClient, type Output } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useZoneFor } from "@/lib/facility/zone";
import {
  DEFAULT_MOVE_IN_WINDOW,
  inWindow,
  MOVE_IN_WINDOWS,
  moveInSortValue,
  type MoveInWindow,
} from "./-components/move-in-filters";
import { PhaseTrack } from "./-components/phase-track";
import { linkClass } from "@/components/app/lists/record-link-shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/_care/move-in/")({
  validateSearch: (search: Record<string, unknown>): { view?: MoveInWindow } => ({
    view: optionalOneOf(
      MOVE_IN_WINDOWS.map((option) => option.value).filter(
        (value) => value !== DEFAULT_MOVE_IN_WINDOW,
      ),
      search.view,
    ),
  }),
  component: MoveInOverview,
});

type Row = Output<typeof orpcClient.care.moveIn.overview>["rows"][number];
const DASH = <span className="text-muted-foreground">-</span>;

function moveInColumns(zoneFor: (facilityId: string | null) => string): ColumnDef<Row>[] {
  return [
    {
      id: "resident",
      header: "Resident",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      // Searched by "first last", ordered by last name.
      sortingFn: (a, b) =>
        String(moveInSortValue(a.original, "resident")).localeCompare(
          String(moveInSortValue(b.original, "resident")),
        ),
      enableHiding: false,
      cell: ({ row: { original: row } }) => (
        <PersonCell
          kind="resident"
          size="sm"
          name={`${row.firstName} ${row.lastName}`}
          renderLink={(label) => (
            <Link
              to="/residents/$residentId/move-in"
              params={{ residentId: row.id }}
              className={cn(linkClass, "flex")}
            >
              {label}
            </Link>
          )}
        />
      ),
    },
    {
      id: "room",
      header: "Room",
      accessorFn: (row) => row.roomNumber ?? "",
      cell: ({ getValue }) => getValue<string>() || DASH,
      meta: { nowrap: true },
    },
    {
      id: "admission",
      header: "Admission date",
      accessorFn: (row) => moveInSortValue(row, "admission"),
      enableGlobalFilter: false,
      meta: { nowrap: true },
      cell: ({ row: { original: row } }) =>
        formatDateOnly(row.admissionDate, zoneFor(row.facilityId)),
    },
    {
      id: "pathway",
      header: "Pathway",
      accessorFn: (row) =>
        row.moveIn
          ? row.moveIn.pathway === "medicaid"
            ? "STAR+PLUS Medicaid"
            : "Private pay"
          : "",
      meta: { nowrap: true, filter: "select", priority: 2 },
      cell: ({ getValue }) => (getValue<string>() ? getValue<string>() : DASH),
    },
    {
      id: "phases",
      header: "Phases",
      enableSorting: false,
      meta: { nowrap: true, priority: 3 },
      cell: ({ row: { original: row } }) =>
        row.moveIn ? <PhaseTrack phases={row.moveIn.phases} /> : DASH,
    },
    {
      // Where the move-in stands: complete, N overdue, or N of M.
      id: "progress",
      header: "Progress",
      accessorFn: (row) => moveInSortValue(row, "progress") ?? -1,
      enableGlobalFilter: false,
      meta: { nowrap: true },
      cell: ({ row: { original: row } }) =>
        row.moveIn ? (
          row.moveIn.complete >= row.moveIn.total ? (
            <Badge variant="ok">Move-in complete</Badge>
          ) : row.moveIn.overdue > 0 ? (
            <FillPill tone="warn">{row.moveIn.overdue} overdue</FillPill>
          ) : (
            <Badge variant="info">
              {row.moveIn.complete}/{row.moveIn.total} done
            </Badge>
          )
        ) : (
          DASH
        ),
    },
    {
      id: "overdue",
      header: "Next / overdue",
      accessorFn: (row) => moveInSortValue(row, "overdue"),
      enableGlobalFilter: false,
      cell: ({ row: { original: row } }) =>
        row.moveIn?.overdue ? (
          <span className="inline-flex items-center gap-1">
            <AlertTriangle className="text-warn size-3.5 shrink-0" aria-hidden="true" />
            <OverflowText>
              {row.moveIn.overdueTitles[0]}
              {row.moveIn.overdueTitles.length > 1
                ? ` +${row.moveIn.overdueTitles.length - 1}`
                : ""}
            </OverflowText>
          </span>
        ) : row.moveIn?.nextDue ? (
          <OverflowText className="text-muted-foreground">
            {row.moveIn.nextDue.title},{" "}
            {formatDateOnly(row.moveIn.nextDue.dueDate, zoneFor(row.facilityId))}
          </OverflowText>
        ) : (
          <span className="text-muted-foreground">All steps done</span>
        ),
    },
  ];
}

/** Everybody moving in, and how far along each one is. */
function MoveInOverview() {
  const { facilityId } = useFacility();
  const overview = useQuery(orpc.care.moveIn.overview.queryOptions({ input: { facilityId } }));

  const navigate = useNavigate({ from: Route.fullPath });
  const view = Route.useSearch().view ?? DEFAULT_MOVE_IN_WINDOW;
  const setView = (next: MoveInWindow) =>
    void navigate({
      search: { view: next === DEFAULT_MOVE_IN_WINDOW ? undefined : next },
      ...KEEP_PLACE,
    });
  const [now] = useState(() => Date.now());
  const all = overview.data?.rows ?? [];
  const totals = overview.data?.totals;
  /* The board is about the people arriving now. Everybody else is one choice
     away rather than gone: an unfinished 2025 move-in is still a real gap,
     just not what somebody opens this screen to work on. */
  const rows = all.filter((row) => inWindow(row, view, now));
  const zoneFor = useZoneFor();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Move-in"
        count={overview.isSuccess ? rows.length : undefined}
        meta="Every step of a correct, complete move-in, verified against the records themselves."
      />
      {totals ? (
        <StatTiles className="xl:grid-cols-4">
          <StatTile label="Moving in" value={totals.arriving} hint="Admission date still ahead" />
          <StatTile
            label="Overdue steps"
            value={totals.overdueSteps}
            hint="Across current move-ins"
          />
          <StatTile label="Due within 48h" value={totals.dueWithin48h} hint="Next step falls due" />
          <StatTile
            label="Move-ins complete"
            value={totals.complete}
            hint="Every step done or waived"
          />
        </StatTiles>
      ) : null}
      {overview.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the move-in board."
            error={overview.error}
            onRetry={() => void overview.refetch()}
            retrying={overview.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={moveInColumns(zoneFor)}
          noun="residents"
          rowId={(row) => row.id}
          pending={overview.isPending}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              searchPlaceholder="Search name or room…"
              trailing={
                <OptionSelect
                  aria-label="Which move-ins"
                  value={view}
                  onChange={(event) => setView(event.target.value as MoveInWindow)}
                >
                  {MOVE_IN_WINDOWS.map((option) => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.label}
                    </SelectOption>
                  ))}
                </OptionSelect>
              }
            />
          )}
          renderRowActions={(row) => (
            <RowActions
              label={`${row.firstName} ${row.lastName}`}
              primary={{
                label: "Open",
                link: { to: "/residents/$residentId/move-in", params: { residentId: row.id } },
              }}
              items={[]}
            />
          )}
          empty={
            all.length ? (
              <EmptyState
                title="Nobody matches this view"
                description="Try another window, or clear the search."
                action={
                  <Button variant="outline" onClick={() => setView("all")}>
                    Show all residents
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Nobody moving in"
                description="No residents in this building yet. Each one's checklist starts at admission."
              />
            )
          }
        />
      )}
    </div>
  );
}
